<template>
  <div class="black-rule-builder">
    <!-- match mode: ALL (AND) / ANY (OR) across every condition -->
    <div class="br-match-line">
      <span>{{ $t('dynamic.MatchWhen') }}</span>
      <a-select v-model="logic" :options="logicOptions" class="br-match-select" @change="emitChange" />
      <span>{{ $t('dynamic.OfTheseConditions') }}</span>
    </div>

    <!-- one row per condition: [Field] ([Header/Query name]) [Operator] [Value] -->
    <div v-for="(cond, index) in conditions" :key="index" class="br-cond-row">
      <span class="br-index">{{ index + 1 }}.</span>
      <a-select v-model="cond.argument" :options="argumentOptions" class="br-arg"
                @change="() => onArgumentChange(cond)" />
      <a-input v-if="isKeyed(cond.argument)" v-model="cond.key" class="br-key"
               :placeholder="keyPlaceholder(cond.argument)" @input="emitChange" />
      <a-select v-model="cond.operator" :options="operatorOptions" class="br-op"
                @change="emitChange" />
      <!-- COUNTRY with an equality operator gets a searchable country picker;
           everything else (incl. COUNTRY with a regex operator) stays free text. -->
      <a-select v-if="useCountryPicker(cond)" v-model="cond.value" class="br-value"
                show-search option-filter-prop="children"
                :placeholder="$t('dynamic.CountryPlaceholder')" @change="emitChange">
        <a-select-option v-for="c in countryOptions" :key="c.code" :value="c.code">
          {{ c.code }} — {{ c.name }}
        </a-select-option>
      </a-select>
      <!-- IS_IDC is a boolean: offer a yes/no picker for the "block proxy/IDC"
           quick action instead of free text. -->
      <a-select v-else-if="useIdcPicker(cond)" v-model="cond.value" class="br-value"
                :placeholder="boolPlaceholder(cond)" @change="emitChange">
        <a-select-option value="true">{{ boolYesLabel(cond) }}</a-select-option>
        <a-select-option value="false">{{ boolNoLabel(cond) }}</a-select-option>
      </a-select>
      <a-input v-else v-model="cond.value" class="br-value"
               :placeholder="$t('dynamic.ValuePlaceholder')" @input="emitChange" />
      <a-tooltip :title="$t('dynamic.RemoveCondition')">
        <a-icon v-if="conditions.length > 1" class="br-remove" type="minus-circle"
                @click="() => removeCondition(index)" />
      </a-tooltip>
    </div>

    <a-button type="dashed" size="small" class="br-add" @click="addCondition">
      <a-icon type="plus" /> {{ $t('dynamic.AddCondition') }}
    </a-button>

    <a-checkbox v-model="negate" class="br-negate" @change="emitChange">
      {{ $t('dynamic.NegateHint') }}
    </a-checkbox>
  </div>
</template>

<style scoped>
.black-rule-builder { padding: 2px 0; }
.br-match-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #555;
}
.br-match-select { width: 130px; }
.br-cond-row {
  display: flex;
  /* wrap instead of overflowing the fixed-width (520px) modal: the three
     controls together need more than the modal's inner width, so let them
     drop to a second line when space runs out. */
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.br-index { width: 16px; color: #999; flex: none; }
/* Controls share the row width. min-width:0 lets the selects shrink below
   their content (truncating with an ellipsis) rather than forcing the row
   past the modal edge; flex-grow lets them fill the row again on wider modals. */
.br-arg { flex: 1 1 130px; min-width: 0; }
.br-key { flex: 1 1 130px; min-width: 0; }
.br-op { flex: 1 1 150px; min-width: 0; }
.br-value { flex: 1 1 160px; min-width: 0; }
.br-remove {
  font-size: 18px;
  color: #bbb;
  cursor: pointer;
  transition: color 0.2s;
  flex: none;
}
.br-remove:hover { color: #ff4d4f; }
.br-add { margin: 4px 0 14px 24px; }
.br-negate { display: block; color: #555; }
</style>

<script>
import countriesRaw from '@/views/domain/CountrySelector/countries.json';

const KEYED_ARGUMENTS = ['HEADER', 'QUERY'];
// AC-2032: ISO 3166-1 alpha-2 codes for the COUNTRY picker. Drop the synthetic
// "all" entry — a blackrule targets specific countries, not everything.
const COUNTRY_OPTIONS = countriesRaw.filter((c) => c.code && c.code !== 'all');

function blankCondition() {
  return { argument: 'METHOD', key: '', operator: '~*', value: '' };
}

export default {
  name: 'DynamicRule',
  props: {
    // The black rule object; we read/emit logic / negate / conditions.
    value: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      logic: 'AND',
      negate: false,
      conditions: [blankCondition()],
    };
  },
  computed: {
    // ALL = AND (every condition must match), ANY = OR (any condition matches).
    logicOptions() {
      return [
        { value: 'AND', label: this.$t('dynamic.MatchAll') },
        { value: 'OR', label: this.$t('dynamic.MatchAny') },
      ];
    },
    // Friendly labels; the stored value stays the engine token.
    argumentOptions() {
      return [
        { value: 'METHOD', label: this.$t('dynamic.argMethod') },
        { value: 'PATH', label: this.$t('dynamic.argPath') },
        { value: 'AGENT', label: this.$t('dynamic.argAgent') },
        { value: 'REFERER', label: this.$t('dynamic.argReferer') },
        { value: 'HEADER', label: this.$t('dynamic.argHeader') },
        { value: 'QUERY', label: this.$t('dynamic.argQuery') },
        { value: 'COUNTRY', label: this.$t('dynamic.argCountry') },
        { value: 'ASN', label: this.$t('dynamic.argAsn') },
        { value: 'ASN_ORG', label: this.$t('dynamic.argAsnOrg') },
        { value: 'CN_PROVINCE', label: this.$t('dynamic.argCnProvince') },
        { value: 'CN_ISP', label: this.$t('dynamic.argCnIsp') },
        { value: 'IS_IDC', label: this.$t('dynamic.argIsIdc') },
        { value: 'IS_UNDER_ATTACK', label: this.$t('dynamic.argUnderAttack') },
      ];
    },
    countryOptions() { return COUNTRY_OPTIONS; },
    operatorOptions() {
      return [
        { value: '==', label: this.$t('dynamic.opEquals') },
        { value: '~=', label: this.$t('dynamic.opNotEquals') },
        { value: '~~', label: this.$t('dynamic.opRegex') },
        { value: '~*', label: this.$t('dynamic.opRegexI') },
      ];
    },
  },
  watch: {
    // Fires when the parent swaps in a different rule object (add/edit modal
    // open). Internal edits mutate the parent's fields without changing the
    // object reference, so this does not re-fire and cannot loop.
    value: { immediate: true, handler(v) { this.hydrate(v); } },
  },
  methods: {
    isKeyed(argument) { return KEYED_ARGUMENTS.includes(argument); },

    // A country dropdown only makes sense for the equality operators; with a
    // regex operator the user types a pattern (e.g. CN|RU) in the text field.
    useCountryPicker(cond) {
      return cond.argument === 'COUNTRY' && (cond.operator === '==' || cond.operator === '~=');
    },

    // Boolean arguments (IS_IDC, IS_UNDER_ATTACK) — show a yes/no picker for the
    // equality operators instead of free text. IS_UNDER_ATTACK is the AC-1939
    // "Under Attack Mode" gate: a rule using it fires only while the domain is under
    // attack (vigil's auto-signal), so a customer can keep strict rules dormant at
    // peacetime and have them engage automatically during an attack.
    useIdcPicker(cond) {
      return (cond.argument === 'IS_IDC' || cond.argument === 'IS_UNDER_ATTACK')
        && (cond.operator === '==' || cond.operator === '~=');
    },

    // Per-argument labels for the shared boolean yes/no picker.
    boolPlaceholder(cond) {
      return cond.argument === 'IS_UNDER_ATTACK'
        ? this.$t('dynamic.UnderAttackPlaceholder') : this.$t('dynamic.IdcPlaceholder');
    },
    boolYesLabel(cond) {
      return cond.argument === 'IS_UNDER_ATTACK'
        ? this.$t('dynamic.UnderAttackYes') : this.$t('dynamic.IdcYes');
    },
    boolNoLabel(cond) {
      return cond.argument === 'IS_UNDER_ATTACK'
        ? this.$t('dynamic.UnderAttackNo') : this.$t('dynamic.IdcNo');
    },

    keyPlaceholder(argument) {
      return argument === 'QUERY'
        ? this.$t('dynamic.QueryKeyPlaceholder')
        : this.$t('dynamic.HeaderKeyPlaceholder');
    },

    hydrate(v) {
      v = v || {};
      this.logic = v.logic === 'OR' ? 'OR' : 'AND';
      this.negate = !!v.negate;
      const conds = Array.isArray(v.conditions) ? v.conditions : [];
      this.conditions = conds.length
        ? conds.map((c) => ({
          argument: c.argument || 'METHOD',
          key: c.key || '',
          operator: c.operator || '~*',
          value: c.value != null ? String(c.value) : '',
        }))
        : [blankCondition()];
    },

    onArgumentChange(cond) {
      // dropping back to a fixed argument clears the stale header/query key
      if (!this.isKeyed(cond.argument)) { cond.key = ''; }
      // a carried-over value like "GET" is meaningless once COUNTRY is chosen
      if (cond.argument === 'COUNTRY' && !/^[A-Z]{2}$/.test(cond.value || '')) {
        cond.value = '';
      }
      // Boolean toggles (IS_IDC, IS_UNDER_ATTACK) — default to "== true" (the quick action)
      if (cond.argument === 'IS_IDC' || cond.argument === 'IS_UNDER_ATTACK') {
        cond.operator = '==';
        if (cond.value !== 'true' && cond.value !== 'false') { cond.value = 'true'; }
      }
      this.emitChange();
    },

    emitChange() {
      // AC-2028: only HEADER/QUERY carry a `key`; the plugin schema rejects an
      // empty key string, so omit it entirely for the fixed arguments.
      const conditions = this.conditions.map((c) => {
        const cond = { argument: c.argument, operator: c.operator, value: c.value };
        if (this.isKeyed(c.argument) && c.key) { cond.key = c.key; }
        return cond;
      });
      this.$emit('input', { logic: this.logic, negate: this.negate, conditions });
    },

    addCondition() {
      if (this.conditions.length >= 8) { return; }
      this.conditions.push(blankCondition());
      this.emitChange();
    },

    removeCondition(index) {
      if (this.conditions.length <= 1) { return; }
      this.conditions.splice(index, 1);
      this.emitChange();
    },
  },
};
</script>
